/**
 * A menu's list of items, shared by the menu bar and context menus: the
 * `menu`/`menuitem` roles, arrow keys moving through enabled items, links for
 * navigation and downloads, and radio items for one-of-several choices.
 */
import { useEffect, useRef } from "react";
import type { KeyboardEvent } from "react";
import "./menu.css";

export interface MenuItem {
  readonly label: string;
  readonly shortcut?: string;
  readonly disabled?: boolean;
  /** Command dispatch or navigation. */
  readonly run?: () => void;
  readonly href?: string;
  readonly download?: boolean;
  /** Present for radio items (one choice among several, like Window › Theme). */
  readonly checked?: boolean;
}

export interface MenuPopupProps {
  /** The menu's accessible name. */
  readonly label: string;
  readonly items: readonly MenuItem[];
  readonly onClose: () => void;
  /** Placement by the host (the menu bar's dropdown). */
  readonly className?: string;
  /** Focus the first item on open, so arrow keys work at once (default); off where several lists show together. */
  readonly autoFocus?: boolean;
}

const ENABLED_ITEMS = "[role^=menuitem]:not([aria-disabled=true])";

export function MenuPopup({ label, items, onClose, className, autoFocus = true }: MenuPopupProps) {
  const listRef = useRef<HTMLDivElement>(null);

  // Move focus into the menu so arrow keys work immediately.
  useEffect(() => {
    if (autoFocus) listRef.current?.querySelector<HTMLElement>(ENABLED_ITEMS)?.focus();
  }, [autoFocus]);

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
    event.preventDefault();
    const enabled = [...(listRef.current?.querySelectorAll<HTMLElement>(ENABLED_ITEMS) ?? [])];
    const index = enabled.indexOf(document.activeElement as HTMLElement);
    const next = event.key === "ArrowDown" ? index + 1 : index - 1;
    enabled[(next + enabled.length) % enabled.length]?.focus();
  };

  return (
    <div
      className={`kn-menu ${className ?? ""}`}
      role="menu"
      aria-label={label}
      ref={listRef}
      onKeyDown={onKeyDown}
    >
      {items.length === 0 && <span className="kn-menu__empty">No items</span>}
      {items.map((item) => (
        <MenuEntry key={item.label} item={item} onClose={onClose} />
      ))}
    </div>
  );
}

function MenuEntry({ item, onClose }: { item: MenuItem; onClose: () => void }) {
  const content = (
    <>
      <span>{item.label}</span>
      {item.shortcut && <span className="kn-menu__shortcut">{item.shortcut}</span>}
    </>
  );

  if (item.href) {
    return (
      <a
        role="menuitem"
        className="kn-menu__item"
        href={item.href}
        onClick={onClose}
        {...(item.download ? { download: "" } : { target: "_blank", rel: "noopener" })}
      >
        {content}
      </a>
    );
  }

  return (
    <button
      type="button"
      className="kn-menu__item"
      aria-disabled={item.disabled === true}
      {...(item.checked === undefined
        ? { role: "menuitem" }
        : { role: "menuitemradio", "aria-checked": item.checked })}
      onClick={() => {
        if (item.disabled) return;
        item.run?.();
        onClose();
      }}
    >
      {content}
    </button>
  );
}
